import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import SectionTitle from '@src/components/SectionTitle';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { trackEvent, trackViewCreatePost } from '@src/metrics';
import { Appearance, Button } from '@src/components/Button';
import { DEFAULT_CURRENCY_SYMBOL, OPENING_PAID_ADDONS_BY_TYPE } from '@src/constants';
import { FUNNEL_PITCH_ROUTE, FUNNEL_ROUTE } from '@src/Routes/links';
import {
  OpeningPaidAddonEnum,
  RecommendedUsersSortOptionEnum,
  usePublishOpeningMutation,
  useSaveOpeningMutation,
} from '@src/graphql/generated';
import { createLink } from '@src/util/createLink';
import { useAppDispatch } from '@src/redux';
import { ActiveView } from '@src/views/Opening/enums/activeView.enum';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { checkSubscriptionState } from '@src/redux/actions/subscription';
import { Tooltip } from '@src/components/Tooltip';
import { SelectBrandingPackage } from './SelectBrandingPackage';
import { SelectNotificationPackage } from './SelectNotificationPackage';
import { BrandingPackageOptions } from './SelectBrandingPackage/props';
import { NotificationPackageOptions } from './SelectNotificationPackage/props';
import { SelectSocialMediaPackage } from './SelectSocialMediaPackage';
import { SocialMediaPackageOptions } from './SelectSocialMediaPackage/props';
import { FunnelCheckoutProps } from './props';
import './styles.scss';

const BRANDING_PRICE = OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.BRANDING_PREMIUM]?.priceInEur || 0;
const NOTIFICATIONS_PRICE = OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.PREMIUM_PUSH]?.priceInEur || 0;
const SOCIAL_MEDIA_PRICE = OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.SOCIAL_MEDIA_PREMIUM]?.priceInEur || 0;

export const FunnelCheckout: FC<FunnelCheckoutProps> = ({ openingId }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [isPublishing, setIsPublishing] = useState<boolean>();
  const [selectedBrandingPackage, setSelectedBrandingPackage] = useState<BrandingPackageOptions>();
  const [selectedSocialMediaPackage, setSelectedSocialMediaPackage] = useState<SocialMediaPackageOptions>();
  const [selectedNotificationsPackage, setSelectedNotificationsPackage] = useState<NotificationPackageOptions>();

  const [saveOpeningMutation] = useSaveOpeningMutation();

  const [publishOpening] = usePublishOpeningMutation({
    onCompleted: () => {
      history.push(
        createLink({
          link: FUNNEL_ROUTE,
          params: { openingId },
          queryParams: {
            activeView: ActiveView.INVITE_CANDIDATES,
            recommendedUsersSortOption: RecommendedUsersSortOptionEnum.RELEVANCE,
          },
        }),
      );
    },
    onError: () => {
      setIsPublishing(false);
      dispatch(setErrorFlashMessage());
    },
  });

  const publish = async () => {
    setIsPublishing(true);
    publishOpening({ variables: { openingId: openingId } });

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.OPENING_PUBLISHED,
      payload: { 'Opening Id': openingId },
    });
  };

  const saveOpeningPaidAddons = async () => {
    const paidAddons = [];

    if (selectedBrandingPackage) {
      paidAddons.push({
        isRecurring: true,
        priceInEur: selectedBrandingPackage === BrandingPackageOptions.PREMIUM ? BRANDING_PRICE : 0,
        type: selectedBrandingPackage,
      });
    }

    if (selectedNotificationsPackage === NotificationPackageOptions.PREMIUM) {
      paidAddons.push({
        isRecurring: true,
        priceInEur: NOTIFICATIONS_PRICE,
        type: NotificationPackageOptions.PREMIUM,
      });
    }

    if (selectedSocialMediaPackage === SocialMediaPackageOptions.SOCIAL_MEDIA) {
      paidAddons.push({
        isRecurring: true,
        priceInEur: SOCIAL_MEDIA_PRICE,
        type: SocialMediaPackageOptions.SOCIAL_MEDIA,
      });
    }

    saveOpeningMutation({
      variables: {
        openingId: openingId,
        openingInput: {
          openingPaidAddons: paidAddons,
        },
      },
    });
  };

  useEffect(() => {
    saveOpeningPaidAddons();
  }, [selectedBrandingPackage, selectedSocialMediaPackage, selectedNotificationsPackage]);

  const calculateTotalPrice = () => {
    let total = 0;

    if (selectedBrandingPackage === BrandingPackageOptions.PREMIUM) {
      total += BRANDING_PRICE;
    }

    if (selectedNotificationsPackage === NotificationPackageOptions.PREMIUM) {
      total += NOTIFICATIONS_PRICE;
    }

    if (selectedSocialMediaPackage === SocialMediaPackageOptions.SOCIAL_MEDIA) {
      total += SOCIAL_MEDIA_PRICE;
    }

    return total;
  };

  const isAllSelected = () => {
    return selectedBrandingPackage && selectedNotificationsPackage && selectedSocialMediaPackage;
  };

  const wrapErrorTooltip = (button: React.ReactElement) => {
    if (isAllSelected()) {
      return button;
    } else {
      return (
        <Tooltip
          placement="top"
          overlay={
            <span>
              Please make a selection or decline
              <br />
              for all of the options above.
            </span>
          }
        >
          {button}
        </Tooltip>
      );
    }
  };

  return (
    <div className="funnel-checkout">
      <div className="funnel-checkout__section">
        <SectionTitle
          subtitle="How would you like your opening to appear. You can increase your chances of a great hire by making sure your opening stands out among the rest."
          title="Select your package"
        />

        <SelectBrandingPackage
          priceInEur={BRANDING_PRICE}
          onSelectPackage={setSelectedBrandingPackage}
          selectedPackage={selectedBrandingPackage}
        />
      </div>

      <div className="funnel-checkout__section">
        <SectionTitle title="Would you like to notify the matching talents?" />

        <SelectNotificationPackage
          openingId={openingId}
          priceInEur={NOTIFICATIONS_PRICE}
          onSelectPackage={setSelectedNotificationsPackage}
          selectedPackage={selectedNotificationsPackage}
        />
      </div>

      <div className="funnel-checkout__section">
        <SectionTitle title="Would you like to add extra distribution through Social Media?" />

        <SelectSocialMediaPackage
          priceInEur={SOCIAL_MEDIA_PRICE}
          onSelectPackage={setSelectedSocialMediaPackage}
          selectedPackage={selectedSocialMediaPackage}
        />
      </div>

      <div className="funnel-checkout__publish-actions">
        {wrapErrorTooltip(
          <div>
            <Button
              isDisabled={isPublishing || !isAllSelected()}
              onClick={() => {
                if (calculateTotalPrice() > 0) {
                  dispatch(
                    checkSubscriptionState({
                      eventSource: MetricEventSource.ACTIVATE_OPENING_BUTTON,
                      isNetworkOnly: true,
                      metricsPrevContext: MetricContext.CREATE_OPENING_REVIEW,
                      nextFunction: publish,
                      onCompleted: publish,
                    }),
                  );
                } else {
                  publish();
                }
              }}
            >
              Publish
            </Button>
          </div>,
        )}

        <Button
          appearance={Appearance.INVISIBLE}
          isLink
          onClick={() =>
            trackViewCreatePost({
              'Event Source': MetricEventSource.BACK_BUTTON,
              'Opening Id': openingId,
              'Previous Context': MetricContext.CREATE_OPENING_REVIEW,
            })
          }
          params={{ openingId }}
          to={FUNNEL_PITCH_ROUTE}
        >
          Back
        </Button>

        <div className="funnel-checkout__total-cost">
          Total cost:{' '}
          <strong>
            {DEFAULT_CURRENCY_SYMBOL}
            {calculateTotalPrice()}/mo
          </strong>
        </div>
      </div>
    </div>
  );
};
